/**
 * AppSelect - a dropdown that matches the Aditor app design instead of the
 * browser's native `<select>` chrome (whose open option list the OS draws and
 * CSS cannot touch). A listbox-pattern control: a styled trigger plus a
 * popover list on the app's elevated surface, keyboard-navigable
 * (Arrow/Enter/Escape/Home/End), closing on an outside pointer-down or Escape.
 *
 * It keeps the same `value` / `onChange(value)` contract as the native element
 * it replaces, so a form behaves identically. Long lists (editors, brands) get
 * a filter box automatically (`> 8` options, override with `searchable`).
 *
 * NOT for the onboarding country picker: that stays native on purpose, where
 * the browser's own autofill and the mobile OS picker are worth more than
 * matching chrome.
 *
 * @module web/components/AppSelect
 */
import { useEffect, useId, useRef, useState } from "react";
import "./app-select.css";

export interface AppSelectOption {
  value: string;
  label: string;
  /** Optional second line under the label (e.g. a role's explanation). */
  description?: string;
}

export function AppSelect({
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel,
  id,
  placeholder = "Select…",
  searchable,
  className,
  searchPlaceholder = "Search…",
  filterLabel = "Filter options",
  noMatchesLabel = "No matches",
}: {
  value: string;
  options: readonly AppSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel?: string | undefined;
  id?: string | undefined;
  placeholder?: string;
  searchable?: boolean | undefined;
  className?: string | undefined;
  /** Search-box texts, for screens that translate (the editor side, spec #304). */
  searchPlaceholder?: string;
  filterLabel?: string;
  noMatchesLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const reactId = useId();
  const baseId = id ?? reactId;

  const canSearch = searchable ?? options.length > 8;
  const filtered =
    canSearch && query.trim()
      ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
      : options;
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && canSearch) searchRef.current?.focus();
  }, [open, canSearch]);

  // Keep the highlighted option scrolled into view during keyboard nav.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  function openMenu() {
    if (disabled) return;
    setQuery("");
    const idx = options.findIndex((o) => o.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
    setOpen(true);
  }
  function close() {
    setOpen(false);
    setActiveIndex(-1);
  }
  function choose(opt: AppSelectOption) {
    onChange(opt.value);
    close();
  }

  function onTriggerKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openMenu();
    }
  }

  function onMenuKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(filtered.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) choose(opt);
    }
  }

  const activeId = activeIndex >= 0 && filtered[activeIndex] ? `${baseId}-opt-${activeIndex}` : undefined;

  return (
    <div className={`app-select${disabled ? " is-disabled" : ""}${className ? ` ${className}` : ""}`} ref={wrapRef}>
      <button
        type="button"
        className="app-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        id={id}
        disabled={disabled}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={`app-select-value${selected ? "" : " is-placeholder"}`}>{selected ? selected.label : placeholder}</span>
        <svg className="app-select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="app-select-menu" onKeyDown={onMenuKeyDown}>
          {canSearch && (
            <input
              ref={searchRef}
              className="app-select-search"
              type="text"
              value={query}
              placeholder={searchPlaceholder}
              aria-label={filterLabel}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
            />
          )}
          <div className="app-select-list" role="listbox" aria-label={ariaLabel} aria-activedescendant={activeId} ref={listRef} tabIndex={-1}>
            {filtered.length === 0 ? (
              <div className="app-select-empty">{noMatchesLabel}</div>
            ) : (
              filtered.map((o, i) => (
                <div
                  key={o.value}
                  id={`${baseId}-opt-${i}`}
                  role="option"
                  aria-selected={o.value === value}
                  data-index={i}
                  className={`app-select-option${i === activeIndex ? " is-active" : ""}${o.value === value ? " is-selected" : ""}`}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => choose(o)}
                >
                  <span className="app-select-option-text">
                    <span className="app-select-option-label">{o.label}</span>
                    {o.description && <span className="app-select-option-desc">{o.description}</span>}
                  </span>
                  {o.value === value && (
                    <svg className="app-select-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
